import { useState, useCallback, useEffect, useRef } from 'react'
import { View, Text, Platform, ActivityIndicator } from 'react-native'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import i18n from '@/lib/i18n/config'
import { yearsAgoISO } from '@/lib/utils/dates'
import { StatusBar } from 'expo-status-bar'
import * as ImagePicker from 'expo-image-picker'
import { compressImage } from '@/lib/utils/image-compress'
import { impact, ImpactStyle, notification, NotificationType } from '@/lib/utils/haptics'
import Animated from 'react-native-reanimated'
import { Camera, CheckCircle2, Smartphone } from '@/components/ui/icons'
import { Avatar } from '@/components/ui/Avatar'
import { Pressable } from '@/components/ui/Pressable'
import { Button } from '@/components/ui/Button'
import { FormActionBar } from '@/components/ui/FormActionBar'
import { PersonNameInput } from '@/components/ui/StructuredTextInput'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { Select } from '@/components/ui/Select'
import { DatePicker } from '@/components/ui/DatePicker'
import { useProfile } from '@/lib/hooks/use-profile'
import { useAuth } from '@/lib/hooks/use-auth'
import { updateProfileSchema } from '@/lib/validations/profile.schema'
import { useSemanticColors, SPACING, FORM_FIELD_GAP } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { useEntrance } from '@/lib/hooks/use-entrance'
import { toast } from '@/lib/toast'
import { captureError, errorMessage } from '@/lib/utils/observability'
import { buildProfileEditInput } from '@/lib/utils/profile-edit-input'
import { webShadow } from '@/lib/theme/web-shadow'

const LOCALE_OPTIONS = [
  { value: 'es' as const, label: 'Español' },
  { value: 'en' as const, label: 'English' },
]

export default function EditProfileScreen() {
  const { t } = useTranslation()
  const enter = useEntrance()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  useAuth() // ensure authenticated context
  const { profile, updateProfile, uploadAvatar, updatePending, uploadAvatarPending } = useProfile()

  const [form, setForm] = useState({
    firstName: profile?.firstName ?? '',
    lastName: profile?.lastName ?? '',
    dateOfBirth: profile?.dateOfBirth ?? (null as string | null),
    preferredLocale: profile?.preferredLocale ?? 'es',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  // The form initializes once at mount via useState.
  // If the profile query was still loading when this screen mounted (e.g.,
  // user navigated here before the profile cache hydrated), every form
  // field defaulted to '' / null. Saving from that state would either
  // (a) overwrite the profile with empty strings (data loss) or (b) skip
  // every field in the diff because '' === ('' from the still-loading
  // profile?.firstName ?? '') evaluates true. Both are bad.
  //
  // This effect resyncs the form ONCE when profile first becomes
  // available. The ref-guard prevents subsequent profile-cache updates
  // (e.g., from successful mutations) from clobbering pending in-progress
  // edits that the user is mid-typing.
  const formInitializedRef = useRef(profile != null)
  useEffect(() => {
    if (profile && !formInitializedRef.current) {
      formInitializedRef.current = true
      setForm({
        firstName: profile.firstName ?? '',
        lastName: profile.lastName ?? '',
        dateOfBirth: profile.dateOfBirth ?? null,
        preferredLocale: profile.preferredLocale ?? 'es',
      })
    }
  }, [profile])

  const updateField = (key: string, value: string | null) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => ({ ...prev, [key]: '' }))
  }

  const handlePickAvatar = useCallback(async () => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images'],
        // Android crop activity is unreliable on many devices — skip cropping
        // and resize programmatically instead for consistent cross-platform behavior
        allowsEditing: Platform.OS === 'ios',
        aspect: Platform.OS === 'ios' ? [1, 1] : undefined,
        quality: 0.8,
      })

      if (result.canceled || !result.assets[0]) return
      const asset = result.assets[0]

      // Center-crop before the square resize so faces are not squashed.
      const crop =
        asset.width && asset.height
          ? (() => {
              const cropSize = Math.min(asset.width, asset.height)
              return {
                originX: Math.max(0, Math.round((asset.width - cropSize) / 2)),
                originY: Math.max(0, Math.round((asset.height - cropSize) / 2)),
                width: cropSize,
                height: cropSize,
              }
            })()
          : undefined
      const compressed = await compressImage(asset.uri, 'avatar', { crop, square: 384 })
      await uploadAvatar(compressed.uri)
      void notification(NotificationType.Success)
      toast.success(t('profile.avatar_updated'))
    } catch (err) {
      // Surface WHICH step failed (read / storage / DB) instead of a generic
      // toast, and report to Sentry — this path used to swallow the cause.
      captureError(err, { screen: 'profile/edit', step: 'avatar_upload' })
      void notification(NotificationType.Error)
      toast.error(errorMessage(err, t('profile.avatar_upload_failed')))
    }
  }, [uploadAvatar, t])

  const handleSave = async () => {
    impact(ImpactStyle.Light)

    const input = buildProfileEditInput(form, profile)

    if (Object.keys(input).length === 0) {
      router.back()
      return
    }

    const result = updateProfileSchema.safeParse(input)
    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      for (const issue of result.error.issues) {
        const key = issue.path[0] as string
        fieldErrors[key] = t(issue.message)
      }
      setErrors(fieldErrors)
      void notification(NotificationType.Error)
      return
    }

    try {
      await updateProfile(result.data)
      // If language changed, update i18n runtime
      if (result.data.preferredLocale) {
        await i18n.changeLanguage(result.data.preferredLocale)
      }
      void notification(NotificationType.Success)
      toast.success(t('profile.profile_updated'))
      router.back()
    } catch (err) {
      if (__DEV__) {
        const e = err as {
          message?: string
          code?: string | number
          details?: string
          hint?: string
          userMessageKey?: string | null
          statusCode?: number
        }
        console.error(
          '[Profile update] FAILED code:',
          e.code ?? '(none)',
          'status:',
          e.statusCode ?? '(none)',
        )
        console.error('[Profile update] FAILED message:', e.message ?? '(no message)')
        if (e.details) console.error('[Profile update] FAILED details:', e.details)
        if (e.hint) console.error('[Profile update] FAILED hint:', e.hint)
        console.error('[Profile update] FAILED userMessageKey:', e.userMessageKey ?? '(none)')
        console.error('[Profile update] FAILED payload:', JSON.stringify(result.data, null, 2))
        console.error('[Profile update] FAILED full error:', err)
      }
      captureError(err, { screen: 'profile/edit', step: 'save' })
      void notification(NotificationType.Error)
      // Use the shared helper so production never surfaces raw Error.message
      // text (Sentry breadcrumb noise, SDK warnings, RLS payloads, etc.).
      toast.error(errorMessage(err, t('profile.update_failed')))
    }
  }

  return (
    <ScreenContainer scroll={false}>
      <StatusBar style={isDark ? 'light' : 'dark'} />
      <View style={{ flex: 1 }}>
        <KeyboardAwareView includeTopInset={false} bottomOffset={SPACING['3xl'] * 2}>
          <ScreenHeader
            title={t('profile.edit_profile')}
            style={{ paddingHorizontal: 0, paddingTop: 0, paddingBottom: SPACING.sm }}
          />

          {/* Avatar picker */}
          <Animated.View
            collapsable={false}
            entering={enter(1)}
            style={{ alignItems: 'center', marginTop: SPACING.lg, marginBottom: SPACING.xl }}
          >
            <Pressable
              onPress={handlePickAvatar}
              disabled={uploadAvatarPending}
              accessibilityLabel={t('profile.change_avatar')}
              testID="edit-profile.avatar"
            >
              <View style={{ position: 'relative', width: 86, height: 86 }}>
                <View
                  style={{
                    width: 86,
                    height: 86,
                    borderRadius: 43,
                    borderWidth: 3,
                    borderColor: isDark ? colors.border : '#FFFFFF',
                    ...webShadow({ y: 2, blur: 8, opacity: 0.15, elevation: 4 }),
                  }}
                >
                  <View style={{ width: 80, height: 80, borderRadius: 40, overflow: 'hidden' }}>
                    <Avatar
                      size="xl"
                      source={profile?.avatarUrl}
                      blurhash={profile?.avatarBlurhash ?? undefined}
                      fallback={profile?.displayName ?? profile?.email ?? '?'}
                      // Seed by user id, exactly like /explore, /profile and
                      // AppTopNav do. Without it this screen hashed the
                      // initials instead and the same person's avatar changed
                      // colour one tap away from their profile.
                      colorSeed={profile?.id}
                      accessibilityLabel={t('profile.change_avatar')}
                    />
                  </View>
                </View>
                <View
                  style={{
                    position: 'absolute',
                    // Pull the badge slightly outside the avatar circle so the
                    // white border reads as a clean punch-out rather than an
                    // edge-clip. -2 on each axis lines the badge centre up
                    // with the 45° tangent point of the avatar circle.
                    bottom: -2,
                    right: -2,
                    zIndex: 10,
                    elevation: 10,
                    width: 28,
                    height: 28,
                    borderRadius: 14,
                    backgroundColor: COLORS.coral.dark,
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderWidth: 2,
                    borderColor: isDark ? colors.surface : '#FFFFFF',
                  }}
                >
                  {uploadAvatarPending ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <Camera size={14} color="#FFFFFF" strokeWidth={2} />
                  )}
                </View>
              </View>
            </Pressable>
            <Text
              style={{
                fontSize: 13,
                fontFamily: FONTS.bodyBold,
                color: colors.textSecondary,
                marginTop: SPACING.sm,
              }}
            >
              {t('profile.change_avatar')}
            </Text>
          </Animated.View>

          {/* Form */}
          <Animated.View collapsable={false} entering={enter(2)}>
            <PersonNameInput
              label={t('auth.first_name')}
              placeholder={t('profile.first_names_placeholder')}
              value={form.firstName}
              onChangeText={(v) => updateField('firstName', v)}
              error={errors.firstName}
              autoComplete="given-name"
              textContentType="givenName"
              returnKeyType="next"
              testID="edit-profile.first-name"
            />
          </Animated.View>

          <Animated.View collapsable={false} entering={enter(3)}>
            <PersonNameInput
              label={t('auth.last_name')}
              placeholder={t('profile.last_names_placeholder')}
              value={form.lastName}
              onChangeText={(v) => updateField('lastName', v)}
              error={errors.lastName}
              autoComplete="family-name"
              textContentType="familyName"
              returnKeyType="next"
              testID="edit-profile.last-name"
            />
          </Animated.View>

          {/* The phone block is a hand-rolled field, so it has to carry the
              same bottom gap the TextInput/Select/DatePicker primitives apply
              from FORM_FIELD_GAP. Without it "Fecha de Nacimiento" sat ~10pt
              under this card while every other label had a full field gap
              above it, and read as a caption on the phone row. */}
          <Animated.View
            collapsable={false}
            entering={enter(3)}
            style={{ marginBottom: FORM_FIELD_GAP }}
          >
            <Text
              style={{
                fontSize: 14,
                fontFamily: FONTS.body,
                letterSpacing: 0.2,
                marginBottom: SPACING.md,
                color: colors.textPrimary,
                opacity: 0.75,
              }}
            >
              {t('auth.phone')}
            </Text>
            <Pressable
              onPress={() => router.push('/(auth)/verify-phone' as never)}
              accessibilityRole="button"
              accessibilityLabel={t('profile.change_verified_phone')}
              testID="edit-profile.phone-verification-link"
              style={({ pressed }) => ({
                minHeight: 58,
                borderRadius: 16,
                borderWidth: 1,
                borderColor: colors.border,
                backgroundColor: colors.surfaceSubtle,
                paddingHorizontal: SPACING.md,
                paddingVertical: SPACING.sm + SPACING.xs,
                flexDirection: 'row',
                alignItems: 'center',
                gap: SPACING.md,
                opacity: pressed ? 0.72 : 1,
              })}
            >
              <View
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 17,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isDark ? 'rgba(239,78,74,0.14)' : 'rgba(239,78,74,0.08)',
                }}
              >
                <Smartphone size={17} color={COLORS.coral.DEFAULT} />
              </View>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text
                  numberOfLines={1}
                  style={{
                    fontSize: 15,
                    fontFamily: FONTS.bodyBold,
                    color: colors.textPrimary,
                  }}
                >
                  {profile?.phone ?? t('profile.phone_not_verified')}
                </Text>
                {/* Two lines, not one: clamped to one this hint died inside
                    the word "cambiar" ("Verificado por código. Tocá para ca…")
                    and never told the user what tapping the row does. The card
                    has the height for two. */}
                <Text
                  numberOfLines={2}
                  style={{
                    marginTop: 2,
                    fontSize: 12,
                    lineHeight: 16,
                    fontFamily: FONTS.body,
                    color: colors.textSecondary,
                  }}
                >
                  {profile?.phone ? t('profile.phone_verified_hint') : t('profile.phone_add_hint')}
                </Text>
              </View>
              {profile?.phone ? <CheckCircle2 size={18} color={COLORS.coral.DEFAULT} /> : null}
            </Pressable>
          </Animated.View>

          <Animated.View collapsable={false} entering={enter(4)}>
            <DatePicker
              label={t('profile.date_of_birth')}
              value={form.dateOfBirth}
              onChange={(d) => updateField('dateOfBirth', d)}
              maxDate={yearsAgoISO(18)}
              error={errors.dateOfBirth}
            />
          </Animated.View>

          <Animated.View collapsable={false} entering={enter(5)}>
            <Select
              label={t('profile.language')}
              options={LOCALE_OPTIONS}
              value={form.preferredLocale}
              onChange={(v) => updateField('preferredLocale', v)}
            />
          </Animated.View>
        </KeyboardAwareView>

        <FormActionBar
          testID="edit-profile.action-bar"
          style={{
            backgroundColor: colors.background,
            borderTopWidth: 1,
            borderTopColor: colors.border,
          }}
        >
          <Button
            title={t('common.save')}
            variant="primary"
            size="lg"
            onPress={handleSave}
            disabled={updatePending}
            loading={updatePending}
            testID="edit-profile.save-button"
          />
        </FormActionBar>
      </View>
    </ScreenContainer>
  )
}
